import React from "react";
import { InstagramSender } from "./api";
import { AdSet, GOALS, InstagramAdState, SPECIAL_CATEGORIES } from "./types";
import { Field, InfoNote, InstagramBadge, RadioCard, SectionHeader, SelectBox, StepFooter, inputCls } from "./ui";
import AdSetDetails from "./ad-set-details";

const SenderAvatar: React.FC<{ letter: string }> = ({ letter }) => (
  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-semibold flex items-center justify-center">{letter}</span>
);

const senderLabel = (s: InstagramSender) => (s.instagram_account?.ig_username ? `@${s.instagram_account.ig_username}` : s.name);

// Step 2 of the Instagram path. Broadcast details and the ad set(s) share this
// page. The channel select lists every broadcast channel so picking a
// different one hands the flow back to the standard Details step. Sender
// Profile is real data from GET /senders/instagram — see api.ts.
const StepDetails: React.FC<{
  state: InstagramAdState;
  update: (patch: Partial<InstagramAdState>) => void;
  channelOptions: { value: string; label: string }[];
  onChannelChange: (value: string) => void;
  senders: InstagramSender[];
  sendersLoading: boolean;
  onSenderChange: (sender: InstagramSender) => void;
  adSets: AdSet[];
  activeAdSet: number;
  setActiveAdSet: (i: number) => void;
  updateAdSet: (patch: Partial<AdSet>) => void;
  addAdSet: () => void;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  savingDraft: boolean;
  nextDisabled?: boolean;
}> = ({
  state,
  update,
  channelOptions,
  onChannelChange,
  senders,
  sendersLoading,
  onSenderChange,
  adSets,
  activeAdSet,
  setActiveAdSet,
  updateAdSet,
  addAdSet,
  onBack,
  onNext,
  onSaveDraft,
  savingDraft,
  nextDisabled,
}) => {
  const senderOptions = senders.map(senderLabel);
  const selectedSender = senders.find((s) => s.id === state.senderId);

  return (
    <div>
      <SectionHeader title="Broadcast Details" subtitle="We have pre-filled this based on your brief. You may edit anything or proceed." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-8">
        <Field label="Broadcast Channel">
          <SelectBox
            value="Instagram"
            onChange={(label) => {
              const hit = channelOptions.find((c) => c.label === label);
              if (hit) onChannelChange(hit.value);
            }}
            options={channelOptions.map((c) => c.label)}
            icon={<InstagramBadge size={26} />}
          />
        </Field>
        {/* No `hint` prop here — it renders between the label and the select
            (see ui.tsx's Field), which would push this select down relative
            to Broadcast Channel's. The equivalent messages go below the
            select instead, like the ads-not-enabled note already did. */}
        <Field label="Sender Profile">
          <SelectBox
            value={selectedSender ? senderLabel(selectedSender) : sendersLoading ? "Loading senders…" : "Select a sender"}
            onChange={(label) => {
              const hit = senders.find((s) => senderLabel(s) === label);
              if (hit) onSenderChange(hit);
            }}
            options={senderOptions}
            icon={<SenderAvatar letter={(selectedSender ? senderLabel(selectedSender) : "@").replace("@", "")[0]?.toUpperCase() || "I"} />}
          />
          {!sendersLoading && senders.length === 0 && (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Connect an Instagram account in Settings to run ads.</p>
          )}
          {selectedSender && selectedSender.instagram_account && !selectedSender.instagram_account.ads_enabled && (
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              This account hasn't granted ads permissions yet — reconnect it in Settings before launching.
            </p>
          )}
        </Field>
        <Field label="Broadcast Goal">
          <SelectBox value={state.goal} onChange={(v) => update({ goal: v })} options={GOALS} />
        </Field>
        <Field label="Broadcast Name">
          <input value={state.name} onChange={(e) => update({ name: e.target.value })} className={inputCls} />
        </Field>
      </div>

      <div className="mt-10">
        <p className="text-base text-gray-800 dark:text-gray-200 mb-3">Buying Type</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <RadioCard selected={state.buyingType === "auction"} onClick={() => update({ buyingType: "auction" })} title="Auction" badge="Recommended">
            Get the best results at the lowest cost
          </RadioCard>
          <RadioCard selected={state.buyingType === "reach"} onClick={() => update({ buyingType: "reach" })} title="Reach and Frequency">
            Pay a fixed price to reach a specific audience
          </RadioCard>
        </div>
      </div>

      <div className="mt-12 md:max-w-[62%]">
        <h3 className="text-2xl text-gray-900 dark:text-gray-100">Special ad categories</h3>
        <Field label="Select the category that best describes what this ad will promote" className="mt-3">
          <SelectBox
            value={state.specialCategory}
            onChange={(v) => update({ specialCategory: v })}
            options={SPECIAL_CATEGORIES}
            placeholder="Declare category if applicable"
          />
        </Field>
        <div className="mt-4">
          <InfoNote icon>
            Accurately declaring your ad categories helps you run ads which are compliant with Instagram advertising standards and helps prevent potential rejections.
          </InfoNote>
        </div>
      </div>

      <div className="mt-12">
        <AdSetDetails adSets={adSets} activeIndex={activeAdSet} setActiveIndex={setActiveAdSet} updateAdSet={updateAdSet} addAdSet={addAdSet} senderId={state.senderId} goal={state.goal} />
      </div>

      <StepFooter onBack={onBack} onNext={onNext} nextLabel="Continue to Messaging" onSaveDraft={onSaveDraft} savingDraft={savingDraft} nextDisabled={nextDisabled} />
    </div>
  );
};

export default StepDetails;
